import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { marketingArchetypeImageMap, marketingArchetypes } from "./archetypeData";
import type { MarketingArchetypeKey } from "./archetypeData";

export type MarketingArchetypeState = "primary" | "secondary" | "default";

type MarketingArchetypeCardProps = {
  archetype: MarketingArchetypeKey;
  state?: MarketingArchetypeState;
  className?: string;
};

const stateConfig: Record<MarketingArchetypeState, string> = {
  primary: "border-primary shadow-sm",
  secondary: "border-secondary-forground",
  default: "border-border hover:border-primary/50",
};

const badgeCopy: Record<MarketingArchetypeState, string> = {
  primary: "Primary",
  secondary: "Secondary",
  default: "",
};

export const MarketingArchetypeCard = ({ archetype, state = "default", className }: MarketingArchetypeCardProps) => {
  const archetypeMeta = marketingArchetypes.find((item) => item.key === archetype);

  if (!archetypeMeta) {
    return null;
  }

  return (
    <Card
      className={cn(
        "relative grid grid-cols-[64px_auto] items-center gap-4 rounded-lg border p-4 transition-all duration-200",
        stateConfig[state],
        className,
      )}
    >
      {state !== "default" && (
        <span className={cn("absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground", state === "secondary" && "bg-secondary text-secondary-foreground border border-solid border-secondary-forground")}>
          {badgeCopy[state]}
        </span>
      )}

      <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full border border-border bg-background">
        <img
          src={`/archetypes/${marketingArchetypeImageMap[archetype]}`}
          alt={archetypeMeta.name}
          className="h-full w-full object-cover"
          loading="lazy"
        />
      </div>

      <div className="space-y-1">
        <p className="text-sm font-semibold text-foreground">{archetypeMeta.name}</p>
        <p className="text-xs text-muted-foreground">{archetypeMeta.description}</p>
      </div>
    </Card>
  );
};

export default MarketingArchetypeCard;
